import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import CodeBlock from "src/components/CodeBlock.js";
import PostPreview from "src/components/PostPreview.js";
import ArticleMarkdown from "src/components/ArticleMarkdown.js";
import { BrowserRouter, Link, Routes, Route, Switch } from "react-router-dom";
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import Latex from 'react-latex';
import 'katex/dist/katex.min.css'; // Import KaTeX CSS

function generateTags(tags) {
    if (tags.length == 0) {
        return "";
    }
    return (
        <p>Tags: {[tags.map((y, j) => {
            return (
                <span key={`tag-${j}`}>
                { !!j && ", "}
                <Link to={`/words/tag/${y}`}>{y}</Link>
                </span>
            );
            })]
        } </p>
    );
}

const ImageRenderer = props => {
  return (
          <img
              loading="lazy"
              src={props.src}
              alt={props.alt}
            />
  );
}

function generateArticle(x, name="words") {
    const renderers = {
        image : ImageRenderer,
        code: CodeBlock
    };
    return (
        <React.Fragment>
            <hgroup>
                <h2>{x.title}</h2>
                { x.subtitle != "" && <subtitle>{x.subtitle}</subtitle>}
                { x.publishTimeUnixSecs > 0 && <p>{new Date(x.publishTimeUnixSecs * 1000).toDateString()}</p> }
            </hgroup>
            <div className={name}>
                <ArticleMarkdown content={x.content} />
            </div>
            { x["paraphernalia"] && (
                        <div className="paraphernalia">
                            <Link to={`/${name}/${x.url}`}>permalink</Link>
                            {generateTags(x.tags)}
                        </div>
            ) }
        </React.Fragment>
    );
}

// ---- /words previews -------------------------------------------------------

// Images whose alt text marks them as inline glyphs/equations rather than
// pictures; these make poor thumbnails.
const NON_THUMBNAIL_ALTS = ["inline", "inline-inheight", "equation"];

// First real image in a post's markdown/HTML, or null. A post can override
// this with an optional "overrideImage" field in index.json.
function findRepresentativeImage(x) {
    if (x.overrideImage) {
        return x.overrideImage;
    }
    const content = x.content || "";
    const pattern = /!\[([^\]]*)\]\(\s*<?([^)\s>]+)>?[^)]*\)|<img\b[^>]*?\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi;
    let match;
    while ((match = pattern.exec(content)) !== null) {
        let alt = match[1];
        if (alt === undefined) {
            const altMatch = match[0].match(/\balt\s*=\s*["']([^"']*)["']/i);
            alt = altMatch ? altMatch[1] : "";
        }
        const altWords = alt.toLowerCase().split(/\s+/);
        if (altWords.some(w => NON_THUMBNAIL_ALTS.includes(w))) {
            continue;
        }
        return match[2] || match[3];
    }
    return null;
}

// Listing used on /words and the tag pages: title, and the start of the post
// fading out (click to expand). See PostPreview.js.
function generatePreview(x, name = "words") {
    const tags = x.tags && x.tags.length > 0
        ? <div className="paraphernalia">{generateTags(x.tags)}</div>
        : null;
    return <PostPreview post={x} name={name} tags={tags} />;
}

/*
Yes, I know this has edge cases where it doesn't work.
It's my responsibility to ensure it remains working because this site is static.
*/
function findArticle(data, url) {
    let found = data.filter(x => x.url == url);
    if (found.length > 0) {
        return found[0];
    }
    return false;
}

function findArticleIndex(data, url) {
    // -1 or first index.
    return data.findIndex(x => x.url == url);
}

// direction is 1 or -1 depending on which way to search.
function findFirstInDirection(data, url, direction) {
    let index = findArticleIndex(data, url);
    if (index == -1) {
        return false;
    }
    index += direction;
    while (index >= 0 && index <= data.length - 1) {
        if (data[index].visibility) {
            return data[index]
        }
        index += direction;
    }
    return
}

function generateForwardBack(data, url) {
    let current = findArticle(data, url);
    let newer = findFirstInDirection(data, url, -1);
    let older = findFirstInDirection(data, url, 1);
    // TODO: fix assumption there will always be a 0.
    if (!current.visibility) {
        return (<div className="bottom-nav"></div>);
    }

    if (data.length == 1) {
        return "";
    }
    return (
        <div className="bottom-nav">
            <div className="half left-half">
                {older && <Link to={`/words/${older.url}`}>&#171; {older.title}</Link>}
            </div>
            <div className="half right-half">
                {newer && <Link to={`/words/${newer.url}`}>{newer.title} &#187;</Link>}
            </div>
        </div>
    );
}


export {
    generateTags,
    generateArticle,
    generatePreview,
    findRepresentativeImage,
    findArticle,
    generateForwardBack
};